// Client error reporter. Captures window.onerror + unhandledrejection,
// dedupes within a session, batches, and ships to the portal's
// recordClientError Cloud Function. Lightweight in-house Sentry — we
// already have Firebase Functions + Firestore, we don't need another
// vendor for the volume one dealership group generates.
//
// Why a custom reporter vs. Sentry/Bugsnag:
//   - One less vendor + dashboard + monthly bill
//   - Errors land in Firestore where the rest of the audit trail lives
//   - Cloud Functions can react to them (alert, auto-create issue, etc.)
//   - We already proxy through portal's functions for everything else
//
// Boots from main.jsx BEFORE any React import so even bootstrap errors
// land. Never throws on its own paths — defensively swallow all failures
// because the last thing we want is the error reporter itself crashing
// the page.

const SESSION_ID = (() => {
  try {
    const k = "sevric.errors.session";
    let s = sessionStorage.getItem(k);
    if (!s) { s = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; sessionStorage.setItem(k, s); }
    return s;
  } catch { return `${Date.now()}-anon`; }
})();

const seen = new Set();      // fingerprint dedupe within a single session
const queue = [];            // pending reports, flushed in a small batch
let flushTimer = null;
let booted = false;

// Stable-ish fingerprint so the same crash doesn't spam Firestore on
// every render. Truncates stack to first frame for stability.
function fingerprintOf(payload) {
  const msg = (payload.message || "").slice(0, 200);
  const first = (payload.stack || "").split("\n")[0] || "";
  return `${msg}|${first}`.slice(0, 280);
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 1500);
}

async function flush() {
  flushTimer = null;
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    // Resolve at call-time so we don't statically import firebase before
    // the bundle has booted. Lets the reporter live BEFORE the rest of
    // the app initializes.
    const [{ getApp }, { getFunctions, httpsCallable }, { getAuth }] = await Promise.all([
      import("firebase/app"),
      import("firebase/functions"),
      import("firebase/auth"),
    ]);
    // The portal-side `recordClientError` callable now requires
    // authentication. If we fire before sign-in completes (e.g. an error
    // during the bootstrap loading screen), the call will reject as
    // unauthenticated and the report is lost. Re-queue and back off so
    // the next flush — after auth lands — picks it up.
    let currentUser = null;
    try { currentUser = getAuth(getApp()).currentUser; } catch {}
    if (!currentUser) {
      // Push the batch back onto the queue, capped to avoid unbounded growth
      // if sign-in never completes (e.g. the page is open at a sign-in screen).
      const MAX_BUFFERED = 50;
      for (const p of batch) {
        if (queue.length >= MAX_BUFFERED) break;
        queue.push(p);
      }
      // Retry in 5s — most sign-ins complete well before that.
      flushTimer = setTimeout(flush, 5000);
      return;
    }
    const fns = getFunctions(getApp(), "us-central1");
    const callable = httpsCallable(fns, "recordClientError");
    // One call per error so a single payload poison-pill doesn't sink
    // the whole batch.
    await Promise.allSettled(batch.map((p) => callable(p)));
  } catch {
    // Swallow — there's nothing useful to do with a failed error report.
    // Don't recurse into the reporter.
  }
}

function record(level, payload) {
  try {
    const p = { ...payload, level, sessionId: SESSION_ID, when: Date.now() };
    const fp = fingerprintOf(p);
    if (seen.has(fp)) return;
    seen.add(fp);
    queue.push(p);
    scheduleFlush();
  } catch { /* defensive */ }
}

// Public entry — called once from main.jsx as early as possible.
export function bootErrorReporter() {
  if (booted) return;
  booted = true;

  // 1. window.onerror catches synchronous JS errors + script load failures.
  window.addEventListener("error", (e) => {
    record("error", {
      message: e?.message || (e?.error && e.error.message) || "unknown",
      stack:   (e?.error && e.error.stack) || "",
      source:  e?.filename || "",
      line:    e?.lineno || 0,
      column:  e?.colno  || 0,
      url:     (typeof location !== "undefined" && location.href) || "",
      ua:      (typeof navigator !== "undefined" && navigator.userAgent) || "",
    });
  });

  // 2. unhandledrejection catches awaited promises that rejected with no .catch().
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason || {};
    record("rejection", {
      message: reason.message || String(reason),
      stack:   reason.stack || "",
      url:     (typeof location !== "undefined" && location.href) || "",
      ua:      (typeof navigator !== "undefined" && navigator.userAgent) || "",
    });
  });

  // 3. Flush remaining errors on tab close (best-effort, uses sendBeacon
  //    semantics indirectly — Firestore writes can't beacon but we still
  //    try a sync-ish flush).
  window.addEventListener("beforeunload", () => {
    if (queue.length > 0) flush();
  });
}

// Public API — call from anywhere to log a non-fatal event.
export function reportClientEvent(message, extra = {}) {
  record("event", {
    message: String(message),
    url: (typeof location !== "undefined" && location.href) || "",
    ua:  (typeof navigator !== "undefined" && navigator.userAgent) || "",
    ...extra,
  });
}
